import { useEffect } from 'react'

/**
 * Measures how long the tracked subject stays on a visible screen. Each
 * flush covers one continuously visible span: it fires when the tab goes
 * hidden (also the last reliable moment before a tab close or a mobile app
 * switch), when the subject changes, or on unmount. Summing the reported
 * seconds gives the total time spent.
 *
 * Pass a `useCallback` keyed on the tracked subject (e.g. the search view or
 * the accommodation) so the measurement restarts exactly when it changes.
 */
export function useVisibleTimeTracking(onTimeSpent: (seconds: number) => void): void {
  useEffect(() => {
    let visibleSince: number | null = document.visibilityState === 'visible' ? Date.now() : null

    const flush = () => {
      if (visibleSince === null) return
      const seconds = Math.round((Date.now() - visibleSince) / 1000)
      visibleSince = null
      if (seconds > 0) {
        onTimeSpent(seconds)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        visibleSince = Date.now()
      } else {
        flush()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      flush()
    }
  }, [onTimeSpent])
}
